import { useEffect, useState } from "react";
import { Button, FormControlLabel, RadioGroup, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as EyeIcon } from "../../../assets/eye.icon.svg";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import { TivaRadioButton } from "../../radio-button/radio-button.comp";
import { CitationDisplay } from "../../../common/enums/citation-display.enum";
import {
    CITATION_DISPLAY_DESCRIPTIONS,
    CITATION_DISPLAY_LABELS,
    CITATION_DISPLAY_ORDER
} from "../../../common/utilities/citation-display.util";
import { SetProviderVisibilityRequest } from "../../../api/admin/content-providers/endpoints-referrers";

type LiveLinkChoice = "linked" | "off";

const ProviderVisibilityDialog = ({
    open,
    onClose,
    providerName,
    showLiveLink,
    initialCitationDisplay,
    initialCitationLiveLink,
    onSaveDefault,
    onResetAll
}: {
    open: boolean;
    onClose: () => void;
    providerName: string;
    // Live spreadsheet deep links apply to Google Drive and OneDrive/SharePoint providers.
    showLiveLink: boolean;
    initialCitationDisplay?: CitationDisplay;
    initialCitationLiveLink?: boolean;
    onSaveDefault: (values: SetProviderVisibilityRequest) => void;
    onResetAll: (values: SetProviderVisibilityRequest) => void;
}) => {
    const [citation, setCitation] = useState<CitationDisplay>(
        initialCitationDisplay ?? CitationDisplay.pagesLinked
    );
    const [liveLink, setLiveLink] = useState<LiveLinkChoice>(
        initialCitationLiveLink ? "linked" : "off"
    );
    const [confirmingReset, setConfirmingReset] = useState<boolean>(false);

    useEffect(() => {
        if (open) {
            setCitation(initialCitationDisplay ?? CitationDisplay.pagesLinked);
            setLiveLink(initialCitationLiveLink ? "linked" : "off");
            setConfirmingReset(false);
        }
    }, [open, initialCitationDisplay, initialCitationLiveLink]);

    const buildValues = (): SetProviderVisibilityRequest => {
        const values: SetProviderVisibilityRequest = {
            defaultCitationDisplay: citation
        };
        if (showLiveLink) {
            values.citationLiveLink = liveLink === "linked";
        }
        return values;
    };

    return (
        <ModalTemplate open={open} width={560} handleClose={onClose} ariaLabel="Default visibility">
            <div className="visibility-dialog">
                <div className="visibility-dialog-heading">
                    <div className="visibility-dialog-title">
                        <SvgIcon
                            className="visibility-dialog-title__icon"
                            component={EyeIcon}
                            inheritViewBox
                        />
                        <span className="visibility-dialog-title__text">Default visibility</span>
                    </div>
                    <span className="visibility-dialog-subtitle">{providerName}</span>
                </div>

                <div className="visibility-dialog-section">
                    <span className="visibility-dialog-section__label">In question responses</span>
                    <RadioGroup
                        className="visibility-dialog-radios"
                        value={citation}
                        onChange={e => setCitation(e.target.value as CitationDisplay)}
                    >
                        {CITATION_DISPLAY_ORDER.map(mode => (
                            <FormControlLabel
                                key={mode}
                                className="visibility-dialog-radio"
                                value={mode}
                                control={<TivaRadioButton />}
                                label={
                                    <span className="visibility-dialog-option">
                                        <span className="visibility-dialog-option__name">
                                            {CITATION_DISPLAY_LABELS[mode]}
                                        </span>
                                        <span className="visibility-dialog-option__desc">
                                            {CITATION_DISPLAY_DESCRIPTIONS[mode]}
                                        </span>
                                    </span>
                                }
                            />
                        ))}
                    </RadioGroup>
                </div>

                {showLiveLink && (
                    <div className="visibility-dialog-section">
                        <span className="visibility-dialog-section__label">
                            Live spreadsheet links
                        </span>
                        <RadioGroup
                            className="visibility-dialog-radios"
                            value={liveLink}
                            onChange={e => setLiveLink(e.target.value as LiveLinkChoice)}
                        >
                            <FormControlLabel
                                className="visibility-dialog-radio"
                                value="off"
                                control={<TivaRadioButton />}
                                label={
                                    <span className="visibility-dialog-option">
                                        <span className="visibility-dialog-option__name">
                                            No live link
                                        </span>
                                        <span className="visibility-dialog-option__desc">
                                            Citations open the stored copy only
                                        </span>
                                    </span>
                                }
                            />
                            <FormControlLabel
                                className="visibility-dialog-radio"
                                value="linked"
                                control={<TivaRadioButton />}
                                label={
                                    <span className="visibility-dialog-option">
                                        <span className="visibility-dialog-option__name">
                                            Also link to the live spreadsheet
                                        </span>
                                        <span className="visibility-dialog-option__desc">
                                            Opens at the cited rows (Google Sheets or Excel for the
                                            web) — viewers need access to the document
                                        </span>
                                    </span>
                                }
                            />
                        </RadioGroup>
                    </div>
                )}

                <div className="visibility-dialog-reset">
                    {confirmingReset ? (
                        <>
                            <span className="visibility-dialog-reset__warning">
                                This overwrites every current file in this source, including
                                per-file customizations. Continue?
                            </span>
                            <div className="visibility-dialog-reset__actions">
                                <Button
                                    className="thirt-button"
                                    variant="text"
                                    onClick={() => setConfirmingReset(false)}
                                >
                                    Keep
                                </Button>
                                <Button
                                    className="primary-button visibility-dialog-error-button visibility-dialog-action__button-margin"
                                    variant="text"
                                    onClick={() => onResetAll(buildValues())}
                                >
                                    Confirm reset
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Button
                            className="visibility-dialog-reset__button"
                            variant="text"
                            onClick={() => setConfirmingReset(true)}
                        >
                            Reset all current files to these values
                        </Button>
                    )}
                </div>

                <div className="visibility-dialog-action">
                    <Button className="thirt-button" variant="text" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        className="primary-button visibility-dialog-action__button-margin"
                        variant="text"
                        onClick={() => onSaveDefault(buildValues())}
                    >
                        Save default
                    </Button>
                </div>
            </div>
        </ModalTemplate>
    );
};

export default ProviderVisibilityDialog;
